import { Column, Popover, Row, Typography } from '@tempmee/react';
import { OutlineChevronDownIcon as ChevronDownIcon } from '@tempmee/react-icons';
import { colors, shadows } from '@tempmee/tailwind';
import { NavLink } from 'react-router';
import { Links } from '~/constants';
import gotuLogo from '~/images/logos/logoLightFull.svg';
import { useCancelPartialSession } from '../useCancelPartialSession';

const ACTIVE_COLOR = colors.success[400];
const INACTIVE_COLOR = colors.shades[0];

const resolveColor = (isActive: boolean) => (isActive ? ACTIVE_COLOR : INACTIVE_COLOR);

const EXPLORE_ITEMS = [
  { label: 'Dental Office', href: Links.temporaryStaffing },
  { label: 'Dental Professional', href: Links.hygienist },
] as const;

const NAV_ITEM_PADDING = '18px 16px';

export interface IAuthHeaderNavProps {
  /**
   * Header chrome variant. `default` shows Log In + Sign Up nav items;
   * `forgot-password` hides those and surfaces "Forgot Password?" as the
   * active item. @default 'default'
   */
  variant?: 'default' | 'forgot-password';
}

export const AuthHeaderNav = ({ variant = 'default' }: IAuthHeaderNavProps = {}) => {
  const cancelPartialSession = useCancelPartialSession();
  const showPrimaryNav = variant === 'default';
  const showForgotPasswordLabel = variant === 'forgot-password';

  return (
    <header data-testid="auth-header-nav" style={{ boxShadow: shadows['elevation-1'] }}>
      <Row
        align="center"
        justify="between"
        px={6}
        height={16}
        style={{ backgroundColor: colors.primary[500] }}
      >
        <NavLink
          to="/login"
          aria-label="GoTu home"
          style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
          onClick={cancelPartialSession}
        >
          <img src={gotuLogo} alt="GoTu" width={88} height={44} style={{ display: 'block' }} />
        </NavLink>

        <Row align="center" gap={2}>
          {showPrimaryNav && (
            <NavLink
              to="/login"
              style={{ padding: NAV_ITEM_PADDING, textDecoration: 'none' }}
              onClick={cancelPartialSession}
            >
              {({ isActive }) => (
                <Typography
                  as="span"
                  bold={isActive}
                  color={resolveColor(isActive)}
                  style={{ fontSize: 18 }}
                >
                  Log In
                </Typography>
              )}
            </NavLink>
          )}

          {showPrimaryNav && (
            <NavLink
              to="/signup"
              style={{ padding: NAV_ITEM_PADDING, textDecoration: 'none' }}
            >
              {({ isActive }) => (
                <Typography
                  as="span"
                  bold={isActive}
                  color={resolveColor(isActive)}
                  style={{ fontSize: 18 }}
                >
                  Sign Up
                </Typography>
              )}
            </NavLink>
          )}

          {showForgotPasswordLabel && (
            <Row as="span" align="center" style={{ padding: NAV_ITEM_PADDING }}>
              <Typography
                as="span"
                bold
                color={ACTIVE_COLOR}
                style={{ fontSize: 18 }}
              >
                Forgot Password?
              </Typography>
            </Row>
          )}

          <Popover
            placement="bottom"
            trigger={
              <Row
                as="span"
                align="center"
                style={{
                  padding: NAV_ITEM_PADDING,
                  cursor: 'pointer',
                  color: INACTIVE_COLOR,
                  gap: 2,
                }}
                role="button"
                tabIndex={0}
              >
                <Typography
                  as="span"
                  color={INACTIVE_COLOR}
                  style={{ fontSize: 18, whiteSpace: 'nowrap' }}
                >
                  Explore GoTu
                </Typography>
                <ChevronDownIcon size={24} color={INACTIVE_COLOR} />
              </Row>
            }
          >
            <Column gap={2} p={4}>
              {EXPLORE_ITEMS.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{ textDecoration: 'none' }}
                >
                  <Typography size="body_large">{item.label}</Typography>
                </a>
              ))}
            </Column>
          </Popover>
        </Row>
      </Row>
    </header>
  );
};
